import { ActionIcon, Flex, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import { DataPersonTct } from '@dealstack/shared/lib/generated';
import {
	mipManagePath,
	personDetailsPath,
} from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { extractCountryCodeFromJurisdictionCountryCode } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { DetailsTileStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsTile.styles.tsx';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';

interface Props {
	dealId: string;
	mipId: string;
	vehicle: DataPersonTct;
}

export const InvestmentVehicleItem = ({ dealId, mipId, vehicle }: Props) => {
	const registrationNumber = vehicle.registrationDetails?.corporateRegistrationNumber?.trim();

	const countryCode = extractCountryCodeFromJurisdictionCountryCode(
		vehicle.registrationDetails?.jurisdiction?.country_code
	);

	// The mip-scoped Investment Vehicle Details page is behind a feature flag; when it is
	// off, the route redirects back to the investors list, so fall back to the deal-wide
	// person profile the arrow used to open.
	const { value: isInvestmentVehicleDetailsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTMENT_VEHICLE_DETAILS
	);
	const targetPath = isInvestmentVehicleDetailsEnabled
		? mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_VEHICLE_DETAILS, {
				dealId,
				mipId,
				personId: vehicle.id,
			})
		: personDetailsPath({ dealId, personId: vehicle.id });

	return (
		<DetailsTileStyles
			data-testid={makeComplexDataTestId(
				TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_INVESTMENT_VEHICLE,
				vehicle.id
			)}
		>
			<Flex align="center" gap="0.5rem">
				<Flex direction="column" flex="1 1 0" miw={0}>
					<Text fz={14} fw={600} c="dark.8" mb="0.25rem" truncate>
						{vehicle.displayName}
					</Text>

					{(!!countryCode || !!registrationNumber) && (
						<Flex align="center" gap="0.25rem" miw={0}>
							{!!countryCode && (
								<ReactCountryFlag
									countryCode={countryCode}
									svg
									style={{ width: '1em', height: '1em', flexShrink: 0 }}
									title={countryCode}
								/>
							)}

							{!!registrationNumber && (
								<Text fz={14} c="dark.0" truncate>
									{registrationNumber}
								</Text>
							)}
						</Flex>
					)}
				</Flex>

				<Link to={targetPath} aria-label={vehicle.displayName}>
					<ActionIcon variant="subtle" size="sm" c="dark.8">
						<Icon variant="arrow-right" size="md" />
					</ActionIcon>
				</Link>
			</Flex>
		</DetailsTileStyles>
	);
};
